import React, { useState } from 'react';
import { Clock, MapPin, Layers, Plus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ActivityExpandPanel } from './ActivityExpandPanel';
import { ActivityQuickAdd } from './ActivityQuickAdd';
import type { Activity } from '@/types/activity.types';

interface ActivityVisualViewProps {
  activities: Activity[];
  eventId: number;
  canManage: boolean;
  onAdded: (title: string) => Promise<void>;
  onAddParallel: (parallelGroup: string, title: string) => Promise<void>;
  onUpdated: (updated: Activity) => void;
  onDeleted: (id: number) => void;
}

type DisplayNode =
  | { type: 'single'; activity: Activity }
  | { type: 'group'; groupKey: string; activities: Activity[] };

/** Build ordered display nodes from flat activity list */
const buildDisplayNodes = (activities: Activity[]): DisplayNode[] => {
  const nodes: DisplayNode[] = [];
  const seen = new Set<number>();

  for (const activity of activities) {
    if (seen.has(activity.id)) continue;

    if (activity.parallel_group) {
      // Collect all siblings in this group
      const siblings = activities.filter((a) => a.parallel_group === activity.parallel_group);
      siblings.forEach((s) => seen.add(s.id));
      nodes.push({ type: 'group', groupKey: activity.parallel_group, activities: siblings });
    } else {
      seen.add(activity.id);
      nodes.push({ type: 'single', activity });
    }
  }
  return nodes;
};

const formatTime = (iso?: string): string => {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
};

const getStatusMeta = (status?: string): { label: string; className: string } => {
  const normalized = (status || 'planned').toLowerCase();

  switch (normalized) {
    case 'draft':
      return { label: 'draft', className: 'bg-gray-100 text-gray-600 border-gray-300' };
    case 'planning':
      return { label: 'planning', className: 'bg-sky-100 text-sky-700 border-sky-300' };
    case 'planned':
      // Backend stores "planned"; product language expects "planning".
      return { label: 'planning', className: 'bg-blue-100 text-blue-700 border-blue-300' };
    case 'ready':
      return { label: 'ready', className: 'bg-indigo-100 text-indigo-700 border-indigo-300' };
    case 'active':
      return { label: 'active', className: 'bg-emerald-100 text-emerald-700 border-emerald-300' };
    case 'completed':
      return { label: 'completed', className: 'bg-green-100 text-green-700 border-green-300' };
    case 'skipped':
      return { label: 'skipped', className: 'bg-amber-100 text-amber-700 border-amber-300' };
    case 'cancelled':
      return { label: 'cancelled', className: 'bg-rose-100 text-rose-700 border-rose-300' };
    case 'published':
      return { label: 'published', className: 'bg-green-100 text-green-700 border-green-300' };
    default:
      return { label: normalized, className: 'bg-gray-100 text-gray-700 border-gray-300' };
  }
};

interface ActivityCardProps {
  activity: Activity;
  eventId: number;
  isExpanded: boolean;
  onToggle: () => void;
  onUpdated: (a: Activity) => void;
  onDeleted: (id: number) => void;
  canManage: boolean;
  seqLabel?: string;
}

const ActivityCard: React.FC<ActivityCardProps> = ({
  activity,
  eventId,
  isExpanded,
  onToggle,
  onUpdated,
  onDeleted,
  canManage,
  seqLabel,
}) => (
  (() => {
    const statusMeta = getStatusMeta(activity.status);

    return (
  <div className="border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow bg-white overflow-hidden">
    {/* Card header — compact, always shown */}
    <div className="px-4 py-3 cursor-pointer hover:bg-gray-50" onClick={onToggle}>
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="flex items-center gap-2 min-w-0">
          {seqLabel && (
            <span className="text-xs font-bold text-gray-400 flex-shrink-0">{seqLabel}</span>
          )}
          <span className="font-semibold text-gray-900 truncate">{activity.title}</span>
        </div>
        <Badge variant="outline" className={`text-xs flex-shrink-0 ${statusMeta.className}`}>
          {statusMeta.label}
        </Badge>
      </div>

      <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
        <span className="flex items-center gap-1">
          <Clock className="h-3 w-3" />
          {formatTime(activity.start_time)}
        </span>
        {activity.location && (
          <span className="flex items-center gap-1">
            <MapPin className="h-3 w-3" />
            {activity.location}
          </span>
        )}
      </div>
    </div>

    {/* Expand panel */}
    {isExpanded && (
      <div className="border-t">
        <ActivityExpandPanel
          activity={activity}
          eventId={eventId}
          isOpen={true}
          onToggle={onToggle}
          onUpdated={onUpdated}
          onDeleted={onDeleted}
          canManage={canManage}
        />
      </div>
    )}
  </div>
    );
  })()
);

export const ActivityVisualView: React.FC<ActivityVisualViewProps> = ({
  activities,
  eventId,
  canManage,
  onAdded,
  onAddParallel,
  onUpdated,
  onDeleted,
}) => {
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [addingParallelToGroup, setAddingParallelToGroup] = useState<string | null>(null);

  const toggleExpand = (id: number) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const nodes = buildDisplayNodes(activities);

  return (
    <div className="flex flex-col items-center gap-0 w-full max-w-2xl mx-auto">
      {nodes.map((node, nodeIndex) => (
        <React.Fragment key={nodeIndex}>
          {/* ── Single activity card ──────────────────────────── */}
          {node.type === 'single' && (
            <div className="w-full">
              <ActivityCard
                activity={node.activity}
                eventId={eventId}
                isExpanded={expandedId === node.activity.id}
                onToggle={() => toggleExpand(node.activity.id)}
                onUpdated={onUpdated}
                onDeleted={onDeleted}
                canManage={canManage}
                seqLabel={String(node.activity.sequence_order)}
              />
            </div>
          )}

          {/* ── Parallel group box ───────────────────────────── */}
          {node.type === 'group' && (
            <div className="w-full border-2 border-purple-200 rounded-xl p-3 bg-purple-50/40">
              <div className="flex items-center gap-2 mb-2">
                <Layers className="h-4 w-4 text-purple-500" />
                <span className="text-xs font-semibold text-purple-600">
                  Parallel Group — {node.groupKey}
                </span>
              </div>

              <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${Math.min(node.activities.length, 3)}, 1fr)` }}>
                {node.activities.map((activity, groupIndex) => {
                  const label = `${activity.sequence_order}${String.fromCharCode(97 + groupIndex)}`;
                  return (
                    <ActivityCard
                      key={activity.id}
                      activity={activity}
                      eventId={eventId}
                      isExpanded={expandedId === activity.id}
                      onToggle={() => toggleExpand(activity.id)}
                      onUpdated={onUpdated}
                      onDeleted={onDeleted}
                      canManage={canManage}
                      seqLabel={label}
                    />
                  );
                })}
              </div>

              {/* Add parallel */}
              {canManage && (
                addingParallelToGroup === node.groupKey ? (
                  <div className="mt-2">
                    <ActivityQuickAdd
                      onAdd={async (title) => {
                        await onAddParallel(node.groupKey, title);
                        setAddingParallelToGroup(null);
                      }}
                    />
                  </div>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-2 text-purple-600 hover:bg-purple-100 text-xs"
                    onClick={() => setAddingParallelToGroup(node.groupKey)}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add Parallel Activity
                  </Button>
                )
              )}
            </div>
          )}

          {/* ── Connector line between nodes ─────────────────── */}
          {nodeIndex < nodes.length - 1 && (
            <div className="flex justify-center w-8 py-1">
              <div className="w-0.5 h-6 bg-gray-300 rounded" />
            </div>
          )}
        </React.Fragment>
      ))}

      {/* ── Quick Add at the bottom ───────────────────────────── */}
      {canManage && (
        <>
          {nodes.length > 0 && (
            <div className="flex justify-center w-8 py-1">
              <div className="w-0.5 h-6 bg-gray-200 rounded" />
            </div>
          )}
          <div className="w-full">
            <ActivityQuickAdd onAdd={onAdded} />
          </div>
        </>
      )}

      {activities.length === 0 && (
        <div className="text-center py-12 text-gray-400 text-sm">
          No activities yet. Add the first one below.
        </div>
      )}
    </div>
  );
};
